import type {
  LegacyHistoricalReportBaseline,
  LegacyHistoricalReportHeader,
  LegacyHistoricalReportSection,
} from "../../types/legacyHistoricalReport";
import type { TemplateSection } from "../../types/reportTemplate";

export function historicalPatientName(
  header: LegacyHistoricalReportHeader
): string {
  return (
    header.patientNameText ||
    [header.patientLastName, header.patientFirstName].filter(Boolean).join(", ")
  );
}

export function buildLegacyHistoricalSections(
  baseline: LegacyHistoricalReportBaseline
): TemplateSection[] {
  const sourceSections = baseline.sections.flatMap((section) => [
    ...historicalNarrativeSections(section),
    ...associatedHistoricalBlocks(baseline, section.id),
  ]);
  return [
    { type: "header", label: "", config: { legacyUseSourceHeader: true } },
    ...sourceSections,
    ...associatedHistoricalBlocks(baseline),
    { type: "diagnostic_map", label: "" },
    { type: "report_supplement_notices", label: "" },
    { type: "addendum", label: "Addendum" },
  ];
}

export function hasCompleteHistoricalSourceHeader(
  baseline: LegacyHistoricalReportBaseline
): boolean {
  if (baseline.sourceHeaderMatchesReviewedIdentity !== true) return false;
  const first = baseline.sections.find((section) => section.text.trim());
  if (first?.kind !== "source_header") return false;
  const source = normalizedHeaderValue(first.text);
  const patient = normalizedHeaderValue(historicalPatientName(baseline.header));
  const caseNumber = normalizedHeaderValue(baseline.header.caseNumber);
  return Boolean(
    patient &&
      caseNumber &&
      source.includes(patient) &&
      source.includes(caseNumber)
  );
}

function normalizedHeaderValue(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export function historicalSectionDisplayText(
  section: LegacyHistoricalReportSection
): string {
  if (section.kind !== "source_footer") return section.text;
  return section.text.replace(
    /^[\t ]*Page[\t ]+\d+[\t ]+of[\t ]+\d+[\t ]*(?:\r?\n|$)/gim,
    ""
  );
}

export function historicalSupplementSections(): TemplateSection[] {
  return [
    { type: "header", label: "" },
    { type: "report_supplement_notices", label: "" },
    { type: "addendum", label: "Addendum" },
  ];
}

function historicalNarrativeSections(
  section: LegacyHistoricalReportSection
): TemplateSection[] {
  const displayText = historicalSectionDisplayText(section);
  if (section.kind === "source_footer" && !displayText.trim()) return [];
  return splitHistoricalText(displayText).map((text, index) => ({
    type: "legacy_historical_block",
    label: index === 0 ? section.label : "",
    config: {
      legacyBlock: {
        kind: "narrative",
        sectionId: section.id,
        text,
        ...(section.kind === "source_header"
          ? { sourceKind: section.kind }
          : {}),
      },
    },
  }));
}

function associatedHistoricalBlocks(
  baseline: LegacyHistoricalReportBaseline,
  sectionId?: string
): TemplateSection[] {
  const matches = (afterSectionId?: string) =>
    sectionId === afterSectionId ||
    (!sectionId && !baseline.sections.some(({ id }) => id === afterSectionId));
  return [
    ...baseline.signatures
      .filter((signature) => matches(signature.afterSectionId))
      .map(
        (signature): TemplateSection => ({
          type: "legacy_historical_block",
          label: "",
          config: {
            legacyBlock: { kind: "signature", signatureId: signature.id },
          },
        })
      ),
    ...baseline.organMaps
      .filter((asset) => matches(asset.afterSectionId))
      .map(
        (asset): TemplateSection => ({
          type: "legacy_historical_block",
          label: "",
          config: { legacyBlock: { kind: "organ_map", assetId: asset.id } },
        })
      ),
  ];
}

export function splitHistoricalText(text: string): string[] {
  const units = Array.from(text);
  const fragments: string[] = [];
  while (units.length > 0) {
    const limit = historicalFragmentLength(units);
    fragments.push(units.splice(0, limit).join(""));
  }
  return fragments.length ? fragments : [""];
}

function historicalFragmentLength(units: string[]): number {
  let lines = 0;
  const limit = Math.min(1200, units.length);
  for (let index = 0; index < limit; index += 1) {
    if (units[index] === "\n") lines += 1;
    if (lines >= 20) return index + 1;
  }
  if (limit === units.length) return limit;
  const boundary = units.slice(0, limit).lastIndexOf(" ");
  return boundary > 600 ? boundary + 1 : limit;
}
